// assets/shipping-quote-service.js
(function () {
  class ShippingQuoteService {
    constructor(form) {
      this.form = form;
      this.qtyInput = form.querySelector('[name="quantity"]');
      this.variantInput = form.querySelector('[name="id"]');

      this.cartId = null;
      this.lineId = null;

      this.debounceTimer = null;
      this.requestToken = 0;

      // Single-flight guard: only one _fetchQuote() network round-trip is
      // ever allowed to be in progress at a time. Without this, an early
      // fetch (page load) and a spuriously-detected "drift" fetch (e.g. a
      // theme script normalizing the quantity input on init, with no
      // visible value change) can overlap, each creating its own Shopify
      // cart via CART_CREATE. Whichever one happens to resolve LAST wins
      // the broadcast — including a transient error from a redundant call
      // that never needed to run — even if a perfectly good result from
      // the other call was already on screen. This is what caused the
      // intermittent "Could not calculate shipping" flash after a
      // successful load, with no user interaction involved.
      this.isFetching = false;
      this.refetchNeeded = false;

      this.lastQty = this._readQty();
      this.lastVariantId = this._readVariantId();

      this._attachListeners();
      this._pollForDriftedChanges();
      this._scheduleQuote(0);
    }

    _readQty() {
      return Math.max(1, parseInt(this.qtyInput?.value, 10) || 1);
    }

    //_readVariantId() {
    //  const raw = this.variantInput?.value;
    //  return raw ? `gid://shopify/ProductVariant/${raw}` : null;
    //}

       _readVariantId() {
     const raw = this.variantInput?.value;
     return /^[1-9]\d*$/.test(raw || '') ? `gid://shopify/ProductVariant/${raw}` : null;
   }

    _attachListeners() {
      ['input', 'change'].forEach(evt => {
        this.qtyInput?.addEventListener(evt, () => this._checkForChange());
        this.variantInput?.addEventListener(evt, () => this._checkForChange());
      });
    }

    _pollForDriftedChanges(interval = 350) {
      setInterval(() => this._checkForChange(), interval);
    }

    _checkForChange() {
      const qty = this._readQty();
      const variantId = this._readVariantId();
      if (qty === this.lastQty && variantId === this.lastVariantId) return;
      this.lastQty = qty;
      this.lastVariantId = variantId;
      this._scheduleQuote();
    }

    _scheduleQuote(delay = 400) {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = setTimeout(() => this._requestQuote(), delay);
    }

    // Single entry point for triggering a quote. If a request is already
    // in flight, this does NOT start a second overlapping one — it just
    // notes that a follow-up is needed, and _fetchQuote's finally block
    // will kick off exactly one more request (with whatever the latest
    // quantity/variant are by then) once the current one has fully
    // settled. This guarantees at most one network round-trip in flight
    // at any time.
    _requestQuote() {
      if (this.isFetching) {
        this.refetchNeeded = true;
        return;
      }
      this._fetchQuote();
    }

    async _fetchQuote() {
      if (!this.lastVariantId) return;
      this.isFetching = true;
      this.refetchNeeded = false;
      const token = ++this.requestToken;

      broadcast({ status: 'loading' });

      try {
        const res = await fetch('https://shipping-estimator.bulk55.workers.dev/', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            cartId: this.cartId,
            lineId: this.lineId,
            merchandiseId: this.lastVariantId,
            quantity: this.lastQty
          })
        });

        if (token !== this.requestToken) return;
        if (!res.ok) throw new Error(`Worker returned ${res.status}`);

        const data = await res.json();
        this.cartId = data.cartId;
        this.lineId = data.lineId;

        broadcast({
          status: 'ok',
          location: data.location,   // { countryCode, zip, region, regionCode } the quote was based on
          rates: data.rates,         // [{ handle, title, estimatedCost }]
          quantity: this.lastQty,
          merchandiseId: this.lastVariantId
        });
      } catch (err) {
        if (token !== this.requestToken) return;
        broadcast({ status: 'error', error: err.message });
      } finally {
        this.isFetching = false;
        // Something changed (or was detected as changed) while this
        // request was in flight. Rather than having raced a second
        // request against this one, go again now that this one has
        // genuinely settled — using whatever the current quantity/variant
        // are at that moment, not stale values captured mid-flight.
        if (this.refetchNeeded) {
          this._fetchQuote();
        }
      }
    }
  }

  function broadcast(detail) {
    window.ShippingQuote._snapshot = detail;
    document.dispatchEvent(new CustomEvent('shipping-quote:update', { detail }));
  }

  // Public API — this is the entire surface any widget needs to know about
  window.ShippingQuote = {
    _snapshot: null,
    subscribe(callback) {
      if (this._snapshot) callback(this._snapshot); // hydrate late subscribers immediately
      const handler = (e) => callback(e.detail);
      document.addEventListener('shipping-quote:update', handler);
      return () => document.removeEventListener('shipping-quote:update', handler);
    }
  };

  document.querySelectorAll('form[action*="/cart/add"]').forEach(form => {
    new ShippingQuoteService(form);
  });
})();